

import React, { useState, useEffect } from 'react';
import { database, auth } from '../firebase';
import { ref, push, set, serverTimestamp } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';
import { Save, X, ArrowLeft } from 'lucide-react';

const PaymentForm = ({ onSuccess, onCancel, editPayment = null }) => {
  const [mode, setMode] = useState(editPayment?.code || '');
  const [description, setDescription] = useState(editPayment?.description || '');
  const [currentUser, setCurrentUser] = useState(null);
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const encodeEmailForPath = (email) => {
    return email.replace(/[.#$[\]]/g, '_');
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setCurrentUser(user);
      } else {
        setCurrentUser(null);
      }
    });

    return () => unsubscribe();
  }, []);

  const handleModeChange = (e) => {
    const value = e.target.value;
    // Allow only letters and spaces, filter out special symbols and numbers
    const filteredValue = value.replace(/[^a-zA-Z\s]/g, '');
    setMode(filteredValue);
  };

  const validateMode = (value) => {
    // Validate that mode contains only letters and spaces
    return /^[a-zA-Z\s]+$/.test(value);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setMessage('');
    setIsSubmitting(true);

    if (!mode || !description) {
      alert('Please fill in all fields.');
      setMessage('Please fill in all fields.');
      setIsSubmitting(false);
      return;
    }

    if (!validateMode(mode)) {
      alert('Payment Mode can only contain letters and spaces.');
      setMessage('Payment Mode can only contain letters and spaces.');
      setIsSubmitting(false);
      return;
    }

    if (!currentUser) {
      alert('You must be logged in to add a Payment Mode.');
      setMessage('You must be logged in to add a Payment Mode.');
      setIsSubmitting(false);
      return;
    }

    try {
      const encodedEmail = encodeEmailForPath(currentUser.email);
      
      if (editPayment) {
        // Update existing payment mode
        const paymentRef = ref(database, `users/${encodedEmail}/paymentModes/${editPayment.id}`);
        set(paymentRef, {
          code: mode,
          description: description,
          createdAt: editPayment.createdAt,
          createdBy: currentUser.email
        })
        .then(() => {
          alert('Payment Mode updated successfully!');
          setMessage('Payment Mode updated successfully!');
          setTimeout(() => {
            onSuccess();
          }, 1500);
        })
        .catch((error) => {
          console.error('Error updating Payment Mode: ', error);
          alert('Error updating Payment Mode.');
          setMessage('Error updating Payment Mode.');
        })
        .finally(() => {
          setIsSubmitting(false);
        });
      } else {
        // Add new payment mode
        const paymentModesRef = ref(database, `users/${encodedEmail}/paymentModes`);
        
        push(paymentModesRef, {
          code: mode,
          description: description,
          createdAt: serverTimestamp(),
          createdBy: currentUser.email
        })
        .then(() => {
          alert('Payment Mode added successfully!');
          setMessage('Payment Mode added successfully!');
          setMode('');
          setDescription('');
          setTimeout(() => {
            onSuccess();
          }, 1500);
        })
        .catch((error) => {
          console.error('Error adding Payment Mode: ', error);
          alert('Error adding Payment Mode.');
          setMessage('Error adding Payment Mode.');
        })
        .finally(() => {
          setIsSubmitting(false);
        });
      }
    } catch (error) {
      console.error('Error:', error);
      alert('An unexpected error occurred.');
      setMessage('An unexpected error occurred.');
      setIsSubmitting(false);
    }
  };

  // Styles with improved alignment
  const containerStyle = {
    maxWidth: '500px',
    margin: '1rem 0 0 0',
    padding: '1.5rem',
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    border: '1px solid #e2e8f0'
  };

  const headerStyle = {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '1.5rem',
    paddingBottom: '0.75rem',
    borderBottom: '1px solid #e2e8f0'
  };

  const headingStyle = {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: '#2563eb',
    margin: '0'
  };

  const inputGroupStyle = {
    marginBottom: '1.25rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch'
  };

  const labelStyle = {
    display: 'block',
    color: '#374151',
    fontWeight: '500',
    fontSize: '0.875rem',
    marginBottom: '0.5rem',
    textAlign: 'left'
  };

  const inputStyle = {
    width: '100%',
    padding: '0.75rem',
    fontSize: '0.875rem',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s, box-shadow 0.2s'
  };

  const buttonStyle = {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    color: 'white',
    fontWeight: '500',
    cursor: 'pointer',
    border: 'none',
    fontSize: '0.875rem',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '0.375rem'
  };

  const saveButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#16a34a'
  };

  const cancelButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#6b7280'
  };

  const buttonContainerStyle = {
    display: 'flex',
    gap: '0.75rem',
    marginTop: '1.5rem',
    justifyContent: 'flex-start'
  };

  const messageStyle = {
    padding: '0.75rem',
    borderRadius: '6px',
    fontWeight: '500',
    fontSize: '0.875rem',
    marginBottom: '1rem'
  };

  const successMessageStyle = {
    ...messageStyle,
    backgroundColor: '#f0fdf4',
    color: '#166534',
    border: '1px solid #bbf7d0'
  };

  const errorMessageStyle = {
    ...messageStyle,
    backgroundColor: '#fef2f2',
    color: '#b91c1c',
    border: '1px solid #fecaca'
  };

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <button
          onClick={onCancel}
          style={{
            padding: '0.25rem',
            borderRadius: '0.25rem',
            border: 'none',
            backgroundColor: 'transparent',
            cursor: 'pointer',
            marginRight: '0.75rem',
            display: 'flex',
            alignItems: 'center'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
          onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          title="Back to Payment Modes"
        >
          <ArrowLeft size={18} color="#6b7280" />
        </button>
        <h1 style={headingStyle}>
          {editPayment ? 'Edit Payment Mode' : 'Add Payment Mode'}
        </h1>
      </div>
      
      {message && (
        <div style={message.includes('Error') ? errorMessageStyle : successMessageStyle}>
          {message}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={inputGroupStyle}>
          <label style={labelStyle}>* Mode</label>
          <input
            type="text"
            value={mode}
            onChange={handleModeChange}
            style={inputStyle}
            placeholder="e.g., CASH, CARD, UPI"
            onFocus={(e) => {
              e.target.style.borderColor = '#3b82f6';
              e.target.style.boxShadow = '0 0 0 2px rgba(59, 130, 246, 0.2)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#e5e7eb';
              e.target.style.boxShadow = 'none';
            }}
          />
          
        </div>
        
        <div style={inputGroupStyle}>
          <label style={labelStyle}>* Description</label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            style={inputStyle}
            placeholder="e.g., Cash Payment, Credit Card"
            onFocus={(e) => {
              e.target.style.borderColor = '#3b82f6';
              e.target.style.boxShadow = '0 0 0 2px rgba(59, 130, 246, 0.2)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#e5e7eb';
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>
        
        <div style={buttonContainerStyle}>
          <button
            type="submit"
            style={{
              ...saveButtonStyle,
              backgroundColor: isSubmitting ? '#86efac' : '#16a34a'
            }}
            onMouseEnter={(e) => {
              if (!isSubmitting) {
                e.target.style.backgroundColor = '#15803d';
              }
            }}
            onMouseLeave={(e) => {
              if (!isSubmitting) {
                e.target.style.backgroundColor = '#16a34a';
              }
            }}
            disabled={isSubmitting}
          >
            <Save size={14} /> {editPayment ? 'Update' : 'Save'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            style={cancelButtonStyle}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#4b5563';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#6b7280';
            }}
          >
            <X size={14} /> Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default PaymentForm;